/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { removeQueryParam } from 'utils/locationHelper';
import { GlobeIcon, LockIcon, UsersMoreIcon } from 'shared/components/Icons';
import { createProject, getProjects, updateProject } from 'app/routes/Dashboard/actions/projects';
import { getError } from 'utils/appHelpers';
import { useAlert } from 'react-alert';
import ManageModal from 'shared/components/modals/ManageModal';
import Input from 'shared/components/Input';
import RadioBox from 'shared/components/RadioBox';
import ProjectPeopleSelection from './ProjectPeopleSelection';
import ProjectImageSelection from './ProjectImageSelection';
import UserWithAvatar from 'shared/components/UserWithAvatar';
import Button from 'shared/components/Button';
import { API, graphqlOperation } from 'aws-amplify';
import { getUsersWithIds } from 'graphql/user/queries';

const shareTypeOptions = [
  { type: 'PUBLIC', icon: GlobeIcon, name: 'share_text_everyone' },
  { type: 'CUSTOM', icon: UsersMoreIcon, name: 'share_some_people' },
  { type: 'PRIVATE', icon: LockIcon, name: 'share_text_me' },
];

const ProjectModal = ({ projectModalId, onClose }) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const projects = useSelector(state => state.projects);
  const history = useHistory();
  const [fetching, setFetching] = useState(false);
  const [friendSelection, setFriendSelection] = useState(false);
  const [imageSelection, setImageSelection] = useState(false);
  const [project, setProject] = useState();
  const [tempImage, setTempImage] = useState({});
  const [tempAddedUsers, setTempAddedUsers] = useState([]);
  const [form, setForm] = useState({
    name: '',
    visibility: '',
  });

  const isNewProjectCreation = !projectModalId || projectModalId === 'new';
  const isSomePeople = form.visibility === 'CUSTOM';

  const getUsersWithID = async ids => {
    if (!ids?.length) return;
    try {
      const body = { ids };
      const { data } = await API.graphql(graphqlOperation(getUsersWithIds, body));
      setTempAddedUsers(data.getUsers);
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const getProjectInfo = async () => {
    const current = projects?.[projectModalId];
    if (!current) return;
    const body = {
      name: current.name,
      visibility: current.visibility,
      icon: current.icon,
    };
    const isCustom = current.visibility === 'CUSTOM';
    if (isCustom) {
      body.visibilityGroup = current.visibilityGroup;
      await getUsersWithID(current.visibilityGroup);
    }
    setForm(body);
    setProject(current);
  };

  const handleTempImageChange = img => {
    setTempImage(img);
  };

  const handleChooseImage = () => {
    setImageSelection(false);
    setForm({ ...form, icon: tempImage });
  };

  const startChooseImage = () => {
    setTempImage(form.icon);
    setImageSelection(true);
  };

  const onFormChange = ({ target }) => {
    const { name, value } = target;
    setForm({ ...form, [name]: value });
  };

  const onVisibilityChange = ({ target }) => {
    const { value } = target;
    const tempForm = { ...form, visibility: value };
    setForm(tempForm);
    if (value !== 'CUSTOM') {
      setTempAddedUsers([]);
      setForm({ ...tempForm, visibilityGroup: [] });
    }
  };

  const handleProjectModalClose = () => {
    if (onClose) onClose();
    else history.push(removeQueryParam('pj'));
  };

  const onFormSubmit = async () => {
    if (isSomePeople && !tempAddedUsers.length) {
      alert.error(t('error_msg_select_at_least_one_user'));
      return;
    }
    try {
      setFetching(true);
      const body = isNewProjectCreation ? { ...form } : { id: projectModalId, ...form };
      isNewProjectCreation ? await createProject(body) : await updateProject(body);
      handleProjectModalClose();
      setFetching(false);
      alert.success(isNewProjectCreation ? t('msg_success_project_create') : t('msg_success_project_update'));
    } catch (err) {
      alert.error(getError(err));
      setFetching(false);
    }
  };

  const onFriendsSave = (users, selectedIDs) => {
    setTempAddedUsers(users);
    setForm({ ...form, visibilityGroup: selectedIDs });
  };

  useEffect(() => {
    if (!projects) getProjects();
  }, []);

  useEffect(() => {
    if (!isNewProjectCreation) getProjectInfo();
  }, [projectModalId, projects]);

  if (friendSelection) {
    return (
      <ProjectPeopleSelection
        setFriendSelection={setFriendSelection}
        onSaveSubmit={onFriendsSave}
        selectedUsers={[...tempAddedUsers]}
        initialUsersIds={[...(form.visibilityGroup || [])]}
      />
    );
  }

  if (imageSelection) {
    return (
      <ManageModal
        paddings='py-15 py-md-30 px-8 px-md-15'
        title={t('title_porject_image')}
        sumbitButton={t('btn_choose')}
        onCloseModal={() => setImageSelection(false)}
        onSubmit={handleChooseImage}
        onBackClick={() => setImageSelection(false)}
      >
        <ProjectImageSelection image={tempImage} setImage={handleTempImageChange} />
      </ManageModal>
    );
  }

  return (
    <ManageModal
      isNewProjectCreation
      title={isNewProjectCreation ? t('project_modal_title_new') : t('project_modal_title_edit')}
      sumbitButton={isNewProjectCreation ? t('btn_create') : t('btn_save')}
      onCloseModal={handleProjectModalClose}
      onSubmit={onFormSubmit}
      isLoading={!isNewProjectCreation && !project}
      isButtonDisabled={fetching}
    >
      <div className='col-12 p-0'>
        <Input
          type='text'
          name='name'
          onChange={onFormChange}
          value={form.name}
          label={t('ph_project_name')}
          placeholder=''
          className='mb-12 mb-md-15'
          required
        />
      </div>
      <div className='mb-15'>
        {form.icon && <label className='fw-700 mb-5 mb-md-6'>{t('label_project_image')}</label>}
        <div className='d-flex align-items-end justify-content-between'>
          {form.icon && <img width={66} height={66} className='rounded-5' src={form.icon} alt='' />}
          <button type='button' className='btn p-0 text-primary text-underline text-md' onClick={startChooseImage}>
            {form.icon ? t('btn_change_project_image') : t('btn_choose_project_image')}
          </button>
        </div>
      </div>
      <div className='col-12 p-0 mb-10'>
        <label className='fw-700 mb-5 mb-md-6'>{t('label_who_can_see')}</label>
        {shareTypeOptions.map(item => {
          const Icon = item.icon;
          return (
            <div key={item.type} className='mb-md-6 mb-3'>
              <RadioBox
                checked={form.visibility === item.type}
                id={`visibility${item.type}`}
                name='visibility'
                value={item.type}
                onChange={onVisibilityChange}
                className='custom-radio-reverse py-0 pl-0 pr-23'
                labelClassName='pl-0'
                required
                label={
                  <div className='d-flex align-items-center'>
                    <div className='icon-border'>
                      <Icon />
                    </div>
                    <span className='ml-9'>{t(item.name)}</span>
                    {isSomePeople && item.type === 'CUSTOM' && (
                      <Button
                        type='button'
                        onClick={() => setFriendSelection(true)}
                        className='text-primary p-0 ml-4 text-sm text-underline'
                      >
                        {t('btn_manage')}
                      </Button>
                    )}
                  </div>
                }
              />
            </div>
          );
        })}
      </div>
      {!!tempAddedUsers.length && (
        <div className='border-top pt-8'>
          <div className='d-flex align-items-center justify-content-between mb-8'>
            <p className='text-gray-dark'>{t('shared_with_people', { count: tempAddedUsers.length })}</p>
            <Button type='button' onClick={() => setFriendSelection(true)} className='text-primary p-0'>
              {t('btn_manage')}
            </Button>
          </div>
          <div className='mb-10 overflow-auto max-h-250'>
            {tempAddedUsers.map((user, i) => {
              const isLast = tempAddedUsers.length - 1 === i;
              return (
                <div
                  key={user.id}
                  className={`d-flex align-items-center justify-content-between ${isLast ? '' : 'mb-7'}`}
                >
                  <UserWithAvatar user={user} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </ManageModal>
  );
};

export default ProjectModal;
